import "./shared/utils/sentry.ts";

import {
  ChakraBaseProvider,
  theme as chakraTheme,
  extendBaseTheme,
  withDefaultColorScheme,
} from "@chakra-ui/react";
import { Theme } from "@radix-ui/themes";
import "@radix-ui/themes/styles.css";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { PostHogProvider } from "posthog-js/react";
import React from "react";
import ReactDOM from "react-dom/client";
import {
  Navigate,
  Outlet,
  RouterProvider,
  createBrowserRouter,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { RecoilRoot } from "recoil";
import RecoilizeDebugger from "recoilize";
import {
  AdminRoot,
  RequiresSuperAdmin,
} from "./admin-new/admin-root.component.tsx";
import { MonthlyCsvExportPage } from "./admin-new/monthly-csv-export-page.component.tsx";
import { StationAdminsPage } from "./admin-new/station-admins-page.component.tsx";
import { AdminStationsPage } from "./admin-new/stations-page.component.tsx";
import { UsersPage } from "./admin/users-page.component.tsx";
import AppPage from "./app-page.component.tsx";
import { AppRoot } from "./app-root.component.tsx";
import { RequiresAuthentication } from "./authentication/core/components/requires-authentication.component.tsx";
import { AccountPage } from "./authentication/core/pages/account-page.component.tsx";
import { LoginPage as CoreLoginPage } from "./authentication/core/pages/login-page.component.tsx";
import { RegisterPage } from "./authentication/core/pages/register-page.component.tsx";
import { RequestPasswordResetPage } from "./authentication/core/pages/request-password-reset-page.component.tsx";
import { ResetPasswordPage } from "./authentication/core/pages/reset-password-page.component.tsx";
import { VerificationSentPage } from "./authentication/core/pages/verification-sent-page.component.tsx";
import { VerifyEmailPage } from "./authentication/core/pages/verify-email-page.component.tsx";
import { LegacyProfile } from "./authentication/legacy/components/legacy-profile.component.tsx";
import { LegacyRequiresAuthentication } from "./authentication/legacy/components/legacy-requires-authentication.component.tsx";
import { LegacyLoginPage } from "./authentication/legacy/legacy-login-page.component.tsx";
import { BookingsRoot } from "./bookings/bookings-root.tsx";
import { BookingsForwardToStationPage } from "./bookings/pages/bookings-forward-to-station-page.tsx";
import { MyBookingsPage } from "./bookings/pages/my-bookings-page.component.tsx";
import CampingBookingRoot from "./camping/camping-booking-root.tsx";
import { CampingRentRoot } from "./camping/camping-rent-root.tsx";
import { CampingRoot } from "./camping/camping-root.component.tsx";
import { CampingBookingSuccessPage } from "./camping/pages/camping-booking-success-page.tsx";
import CampingBookingUnlockPage from "./camping/pages/camping-booking-unlock-page.tsx";
import { CampingRentBikeSelectionPage } from "./camping/pages/camping-rent-bike-selection-page.js";
import { CampingRentCapturePage } from "./camping/pages/camping-rent-capture-page.tsx";
import { CampingRentPage } from "./camping/pages/camping-rent-page/camping-rent-page.component.tsx";
import "./index.css";
import { MainRoot } from "./main-root.tsx";
import { MapRoot } from "./map/map-root.component.tsx";
import { MapPage } from "./map/pages/map-page.component.tsx";
import { routes } from "./routes.ts";
import { AppHandoffPage } from "./shared/components/app-handoff-page.component.tsx";
import { NotFound } from "./shared/components/not-found.tsx";
import { QueryProvider } from "./shared/tanstack-query/query-provider.component.tsx";
import {
  isNativeIOSPlatform,
  isSafariBrowser,
  isWebPlatform,
} from "./shared/utils/environment.ts";
import { posthogConfig } from "./shared/utils/posthog.ts";
import { UnitRentCapturePage } from "./units/pages/unit-rent-capture-page.tsx";
import { UnitRentPage } from "./units/pages/unit-rent-page/index.tsx";
import { UnitRoot } from "./units/unit-root.component.tsx";
import { UnlockStationPageComponent } from "./unlock/unlock-station-page.component.tsx";

const root = document.getElementById("root");
if (!root) throw new Error("No root element found");

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <MainRoot>
        <Outlet />
      </MainRoot>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/map" replace />,
      },
      {
        path: "/scan",
        element: (
          <AppRoot>
            <LegacyProfile />
            <AppPage />
          </AppRoot>
        ),
      },
      {
        path: routes.appHandoff,
        element: isWebPlatform() ? (
          <AppHandoffPage />
        ) : (
          <Navigate to={routes.scan} replace />
        ),
      },
      {
        path: "/map",
        element: (
          <MapRoot>
            <MapPage />
          </MapRoot>
        ),
      },
      {
        path: "/stations/:stationId/unlock",
        element: (
          <AppRoot>
            <LegacyProfile />
            <UnlockStationPageComponent />
          </AppRoot>
        ),
      },
      {
        path: "/stations/:stationSlug",
        element: (
          <CampingRoot>
            <Outlet />
          </CampingRoot>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="rent" replace />,
          },
          {
            path: "rent",
            element: (
              <CampingRentRoot>
                <Outlet />
              </CampingRentRoot>
            ),
            children: [
              {
                index: true,
                element: <CampingRentPage />,
              },
              {
                path: "bikes",
                element: <CampingRentBikeSelectionPage />,
              },
              {
                path: "capture",
                element: <CampingRentCapturePage />,
              },
            ],
          },
          {
            path: "bookings/:bookingSlug",
            element: (
              <CampingBookingRoot>
                <Outlet />
              </CampingBookingRoot>
            ),
            children: [
              {
                index: true,
                element: <CampingBookingUnlockPage />,
              },
              {
                path: "success",
                element: <CampingBookingSuccessPage />,
              },
            ],
          },
        ],
      },
      {
        path: "/units/:unitSlug",
        element: (
          <UnitRoot>
            <Outlet />
          </UnitRoot>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="rent" replace />,
          },
          {
            path: "rent",
            element: <Outlet />,
            children: [
              {
                index: true,
                element: <UnitRentPage />,
              },
              {
                path: "capture",
                element: <UnitRentCapturePage />,
              },
            ],
          },
        ],
      },
      {
        path: "/authentication/login",
        element: (
          <AppRoot>
            <LegacyProfile />
            <LegacyLoginPage />
          </AppRoot>
        ),
      },
      {
        path: "/auth",
        element: (
          <AppRoot>
            <Outlet />
          </AppRoot>
        ),
        children: [
          {
            path: "register",
            element: <RegisterPage />,
          },
          {
            path: "login",
            element: <CoreLoginPage />,
          },
          {
            path: "request-password-reset",
            element: <RequestPasswordResetPage />,
          },
          {
            path: "reset-password",
            element: <ResetPasswordPage />,
          },
          {
            path: "verification-sent",
            element: <VerificationSentPage />,
          },
          {
            path: "verify-email",
            element: <VerifyEmailPage />,
          },
        ],
      },
      {
        path: "/account",
        element: (
          <AppRoot>
            <RequiresAuthentication>
              <AccountPage />
            </RequiresAuthentication>
          </AppRoot>
        ),
      },
      {
        path: "/bookings",
        element: (
          <BookingsRoot>
            <Outlet />
          </BookingsRoot>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="my" replace />,
          },
          {
            path: "my",
            element: <MyBookingsPage />,
          },
          {
            path: ":bookingSlug",
            element: <BookingsForwardToStationPage />,
          },
        ],
      },
      {
        path: "admin",
        element: (
          <AppRoot>
            <LegacyProfile />
            <LegacyRequiresAuthentication>
              <Outlet />
            </LegacyRequiresAuthentication>
          </AppRoot>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="users" replace={true} />,
          },
          {
            path: "users",
            element: <UsersPage />,
          },
        ],
      },
      {
        path: "admin-new",
        element: (
          <RequiresAuthentication>
            <AdminRoot />
          </RequiresAuthentication>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="stations" replace />,
          },
          {
            path: "stations",
            element: <AdminStationsPage />,
          },
          {
            path: "exports",
            element: <MonthlyCsvExportPage />,
          },
          {
            path: "admins",
            element: (
              <RequiresSuperAdmin>
                <StationAdminsPage />
              </RequiresSuperAdmin>
            ),
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: <NotFound />,
  },
]);

const {
  Badge,
  Button,
  Checkbox,
  Drawer,
  Input,
  FormError,
  FormLabel,
  Alert,
  Heading,
  Spinner,
  Table,
  Modal,
  Select,
  Tabs,
  Link,
  Tooltip,
  CloseButton,
} = chakraTheme.components;

const theme = extendBaseTheme(
  {
    components: {
      Badge,
      Button,
      Checkbox,
      Drawer,
      Input,
      FormError,
      FormLabel,
      Alert,
      Heading,
      Spinner,
      Table,
      Modal,
      Select,
      Tabs,
      Link,
      Tooltip,
      CloseButton,
    },
    styles: {
      global: {
        "#root": {
          minHeight: "100vh",
          textAlign: "left", // overwriting fixes e.g. error message alignment
        },
      },
    },
  },
  withDefaultColorScheme({ colorScheme: "blue" }),
);

const isIOSOrSafari = isNativeIOSPlatform() || isSafariBrowser();

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <RecoilRoot>
      {/* On Safari and iOS, the debugger might cause issues e.g. the page breaks
      when choosing a value in a dropdown on the rent page. `Error: Snapshot has already been
      released.` */}
      {import.meta.env.DEV && !isIOSOrSafari && <RecoilizeDebugger />}
      <QueryProvider>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
        <ChakraBaseProvider theme={theme}>
          <Theme>
            <PostHogProvider {...posthogConfig}>
              <RouterProvider router={router} />

              <ToastContainer theme="dark" />

              {/* <ThemePanel /> */}
            </PostHogProvider>
          </Theme>
        </ChakraBaseProvider>
      </QueryProvider>
    </RecoilRoot>
  </React.StrictMode>,
);
